/**
 * useStaffTileCounts - the numbers behind the little app-style badges on
 * the staff home tile grids and the nav drawer rows (Bobbie: "when
 * something new has happened can they get a small number badge next to it
 * like an app would?").
 *
 * Every count here re-reads listeners the staff home already pays for. The
 * shared message waiting hook supplies the participant-filtered message
 * count, while the task and sub hooks match StaffAttentionCard and
 * HomeTaskSummary. Firestore shares those watch targets, so no new
 * studio-wide listener is opened. Since the perf round the task slice is
 * the bounded assignee-scoped query (limit 150), not the whole task board.
 * Mount this ONLY in staff-role
 * component trees (the same rule StaffAttentionCard follows): the message
 * collection-group and subRequests queries are not provable for family
 * accounts.
 *
 * While a task or sub source is still loading (or errored - the underlying
 * hooks reset to empty on error), its count reports 0 and the badge simply
 * does not render. A missing badge is always safer than a wrong number.
 *
 * THE MESSAGES COUNT IS THE EXCEPTION, and says so. A failed message check
 * used to leave this tile quietly showing a partial number, or no badge at
 * all, while something was still waiting (Bobbie, 2026-09-29: "it says i'm
 * all caught up"). `messagesFailed` carries that, and the tile draws "?" or
 * "3+" instead of a false zero. A FULL must-read window is not a failure and
 * shows the number found (see WaitingCountState).
 */

import { useMemo } from 'react';
import { useAuth } from './auth-context';
import { type TaskDoc } from './tasks';
import { useMyOpenTasksStillOpenable } from './bagItemTaskLive';
import { claimableSubCount, useUpcomingSubRequests, type SubRequestDoc } from './subs';
import { useUserDoc } from './users';
import { AWARD_TASK_ID_PREFIX } from './studentAwards';
import { useMessageWaitingRows, type MessageWaitingResult } from './messageWaiting';
import { waitingCheckFailed } from './staffWaitingOnYouCore';

export interface StaffTileCounts {
  /** Messages tile: every row Messages shows as unread (B17), the same
   *  number the home Waiting on you row, the bell and the badge show. Staff
   *  announcements are inside it; nothing is added on top. */
  messages: number;
  /** The rows behind `messages`, handed to StaffAttentionCard so the home
   *  mounts the shared count hook once (screen-read-budget.test.mjs). */
  messageWaiting: MessageWaitingResult;
  /** True when a message check failed, so `messages` is only what survived.
   *  Passed straight to NavTile. */
  messagesFailed: boolean;
  /** Tasks tile: my open tasks (assigned to me, not done, not snoozed). */
  tasks: number;
  /** Sub board tile: open requests someone else posted that I could claim. */
  subs: number;
  /** Student Awards tile: my open award-nomination reminder tasks, spotted
   *  by their deterministic `studentAwards_` doc-id prefix. */
  awards: number;
}

/**
 * Perf round (home-listener-dedupe): DirectorCard, ActiveMemberCard and
 * AdminHomeCard all mount StaffAttentionCard right next to this hook and
 * used to each open their own copy of the exact same assigned-tasks and
 * subRequests listeners this hook also opened - two listeners, three
 * times each, on every staff home load. Pass the SAME arrays those
 * components already fetched and this hook skips subscribing on its own.
 * Omit both (NavDrawer's badge rows have no sibling StaffAttentionCard to
 * share with) and it falls back to its own hooks exactly as before.
 */
export interface StaffTileCountInputs {
  openTasks?: TaskDoc[];
  tasksLoading?: boolean;
  subs?: SubRequestDoc[];
  subsLoading?: boolean;
  subsError?: string | null;
}

export function useStaffTileCounts(inputs?: StaffTileCountInputs): StaffTileCounts {
  const { user } = useAuth();
  // The role decides whether the sub count means anything at all. Shared
  // hook, same doc every staff surface already reads; no new listener.
  const { userDoc, loading: roleLoading } = useUserDoc();
  const role = userDoc?.role ?? null;
  const messageWaiting = useMessageWaitingRows({ mutedIds: userDoc?.notifPrefs?.mutedIds, staff: true, userDoc });
  const tasksProvided = inputs?.openTasks !== undefined;
  const subsProvided = inputs?.subs !== undefined;
  // enabled = !provided: the hook call itself always happens (Rules of
  // Hooks), but enabled=false skips the actual onSnapshot subscribe - see
  // useMyOpenTasks/useUpcomingSubRequests' own `enabled` param. 99, not the
  // default 3, on the fallback path: the tile needs the real open count
  // (display caps at "9+" anyway) and the awards filter below needs the
  // whole list.
  const { loading: fetchedTasksLoading, tasks: fetchedOpenTasks } =
    useMyOpenTasksStillOpenable(99, !tasksProvided);
  const { loading: fetchedSubsLoading, requests: fetchedSubs, error: fetchedSubsError } =
    useUpcomingSubRequests(!subsProvided);
  const openTasks = inputs?.openTasks ?? fetchedOpenTasks;
  const tasksLoading = tasksProvided ? (inputs?.tasksLoading ?? false) : fetchedTasksLoading;
  const subs = inputs?.subs ?? fetchedSubs;
  const subsLoading = subsProvided ? (inputs?.subsLoading ?? false) : fetchedSubsLoading;
  const subsError = subsProvided ? (inputs?.subsError ?? null) : fetchedSubsError;
  const meUid = user?.uid ?? '';

  return useMemo(() => {
    // Uses the same participant-thread filter as the bell and the home
    // "Waiting on you" card. Unrelated class traffic can no longer make one
    // of these three surfaces disagree with the others.
    const messages = messageWaiting.count;
    const messagesFailed = waitingCheckFailed(messageWaiting);

    const tasks = tasksLoading ? 0 : openTasks.length;

    const awards = tasksLoading
      ? 0
      : openTasks.filter((t) => t.id.startsWith(AWARD_TASK_ID_PREFIX)).length;

    /* WHO CAN ACTUALLY CLAIM, not merely who can read. This used to count
       every open request the viewer could see, so the Admin desk - which
       reads every tier and has no Claim button anywhere - was told "6 Sub
       requests you could claim" and then handed a confirmed-only board with
       nothing on it. See canClaimSubRequests in subs.ts for the report that
       found it. A tile that overcounts is worse than no tile: it sends
       somebody to a screen that cannot answer for the number. */
    const claimableSubs =
      subsLoading || subsError || roleLoading
        ? 0
        : claimableSubCount(subs, meUid, role);

    return { messages, messagesFailed, messageWaiting, tasks, subs: claimableSubs, awards };
  }, [
    messageWaiting,
    tasksLoading,
    openTasks,
    subsLoading,
    subsError,
    subs,
    meUid,
    role,
    roleLoading,
  ]);
}
